import React, { useState, useRef, useEffect } from 'react';
import { useStore } from '../store/useStore';
import { scaleChipLabel } from '../utils/measure';
import styles from './Toolbar.module.css';

// 'text' is no longer a top-level toolbar slot — it now lives inside the
// text flyout (alongside callout + cloud-callout). 'line' / 'polyline' /
// 'freehand' likewise share the line flyout. 'rect' / 'polygon' /
// 'ellipse' share the shape flyout. The flyout button itself occupies
// the slot in the toolbar.
const TOOLS = [
  { id: 'select',   label: 'Select',   icon: SelectIcon,   readonlyOk: true },
  { id: 'pan',      label: 'Pan',      icon: PanIcon                        },
  { id: 'arrow',    label: 'Arrow',    icon: ArrowIcon                      },
  { id: 'cloud',    label: 'Cloud',    icon: CloudIcon                      },
];

// Sub-tools that share the Text flyout slot. Same flyout pattern as the
// measurement tools; one slot in the toolbar, three options in the menu.
const TEXT_TOOLS = [
  { id: 'text',          label: 'Text',          icon: TextIcon,        sub: 'A plain text label.' },
  { id: 'callout',       label: 'Callout',       icon: CalloutIcon,     sub: 'Text box with leader + arrow.' },
  { id: 'cloud-callout', label: 'Cloud Callout', icon: CloudCalloutIcon, sub: 'Cloud bubble with leader.' },
];

// Line variants share a single toolbar slot — same flyout pattern as
// text/measure. Plain line is the two-point click-click; polyline is
// click-to-add-vertex (Enter / dbl-click finalizes); freehand follows
// the cursor while the pointer is held.
const LINE_TOOLS = [
  { id: 'line',     label: 'Line',     icon: LineIcon     },
  { id: 'polyline', label: 'Polyline', icon: PolylineIcon },
  { id: 'freehand', label: 'Freehand', icon: FreehandIcon },
];

// Closed-shape variants — drag-out rect / click-vertex polygon /
// drag-out ellipse share one slot. Selected variant's icon is
// shown on the slot button.
const SHAPE_TOOLS = [
  { id: 'rect',    label: 'Rect',    icon: RectIcon    },
  { id: 'polygon', label: 'Polygon', icon: PolygonIcon },
  { id: 'ellipse', label: 'Ellipse', icon: EllipseIcon },
];

// Measurement tools are grouped behind a single toolbar slot that flies out a
// menu to the LEFT of the toolbar. Length/area/volume each have 2-3 kinds.
const MEASURE_GROUPS = [
  {
    group: 'Length',
    tools: [
      { id: 'measure-line',      label: 'Line',      icon: LineIcon },
      { id: 'measure-polyline',  label: 'Polyline',  icon: PolylineIcon },
      { id: 'measure-freeline',  label: 'Freeline',  icon: FreehandIcon },
    ],
  },
  {
    group: 'Area',
    tools: [
      { id: 'measure-rect-area',     label: 'Rect',     icon: RectIcon },
      { id: 'measure-polygon-area',  label: 'Polygon',  icon: PolygonIcon },
      { id: 'measure-freeform-area', label: 'Freeform', icon: FreehandIcon },
    ],
  },
  {
    group: 'Volume',
    tools: [
      { id: 'measure-rect-volume',     label: 'Rect',     icon: RectIcon },
      { id: 'measure-polygon-volume',  label: 'Polygon',  icon: PolygonIcon },
      { id: 'measure-freeform-volume', label: 'Freeform', icon: FreehandIcon },
    ],
  },
];

const PRESET_COLORS = [
  '#e8a020', '#4a9eff', '#3ecf8e', '#ff5c5c',
  '#9b8aff', '#f5c842', '#ff9040', '#ffffff',
  '#808080', '#000000',
];

export default function Toolbar() {
  const {
    activeTool, setActiveTool,
    activeColor, setActiveColor,
    activeFillColor, setActiveFillColor,
    activeFillOpacity, setActiveFillOpacity,
    zoom, setZoom,
    role,
    activeSheet,
    setCalibrationModalOpen,
  } = useStore();

  const [editingColor, setEditingColor] = useState(null); // 'fill' | 'stroke' | null
  const [measureMenuOpen, setMeasureMenuOpen] = useState(false);
  const [textMenuOpen, setTextMenuOpen]       = useState(false);
  const [lineMenuOpen, setLineMenuOpen]       = useState(false);
  const [shapeMenuOpen, setShapeMenuOpen]     = useState(false);
  const colorSectionRef  = useRef(null);
  const measureBtnRef    = useRef(null);
  const textBtnRef       = useRef(null);
  const lineBtnRef       = useRef(null);
  const shapeBtnRef      = useRef(null);
  const customStrokeRef  = useRef(null);
  const customFillRef    = useRef(null);

  // Any signed-in role can use markup tools — field users get private
  // markups (visible only to themselves), admin / pm / super share drafts
  // and publish. Server enforces the privacy boundary so this just gates
  // UI affordance.
  const canMarkup    = !!role;
  const calibration  = activeSheet?.calibration || null;
  const hasScale     = !!calibration?.pagePointsPerFoot;
  const measureIds   = MEASURE_GROUPS.flatMap(g => g.tools.map(t => t.id));
  const measureActive = measureIds.includes(activeTool);
  const textIds      = TEXT_TOOLS.map((t) => t.id);
  const textActive   = textIds.includes(activeTool);
  const lineIds      = LINE_TOOLS.map((t) => t.id);
  const lineActive   = lineIds.includes(activeTool);
  const shapeIds     = SHAPE_TOOLS.map((t) => t.id);
  const shapeActive  = shapeIds.includes(activeTool);
  // Icon shown on each collapsed flyout slot tracks the active variant
  // so the toolbar reflects the user's last pick.
  const ActiveLineIcon  = (LINE_TOOLS.find((t)  => t.id === activeTool)?.icon) || LineIcon;
  const ActiveShapeIcon = (SHAPE_TOOLS.find((t) => t.id === activeTool)?.icon) || RectIcon;

  useEffect(() => {
    if (!editingColor) return;
    function onDown(e) {
      if (colorSectionRef.current && !colorSectionRef.current.contains(e.target)) {
        setEditingColor(null);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [editingColor]);

  useEffect(() => {
    if (!measureMenuOpen) return;
    function onDown(e) {
      if (measureBtnRef.current && !measureBtnRef.current.contains(e.target)) {
        setMeasureMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [measureMenuOpen]);

  // Same outside-click pattern for the text flyout.
  useEffect(() => {
    if (!textMenuOpen) return;
    function onDown(e) {
      if (textBtnRef.current && !textBtnRef.current.contains(e.target)) {
        setTextMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [textMenuOpen]);

  useEffect(() => {
    if (!lineMenuOpen) return;
    function onDown(e) {
      if (lineBtnRef.current && !lineBtnRef.current.contains(e.target)) {
        setLineMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [lineMenuOpen]);

  useEffect(() => {
    if (!shapeMenuOpen) return;
    function onDown(e) {
      if (shapeBtnRef.current && !shapeBtnRef.current.contains(e.target)) {
        setShapeMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [shapeMenuOpen]);

  function pickTextTool(id) {
    setActiveTool(id);
    setTextMenuOpen(false);
  }

  function pickLineTool(id) {
    setActiveTool(id);
    setLineMenuOpen(false);
  }

  function pickShapeTool(id) {
    setActiveTool(id);
    setShapeMenuOpen(false);
  }

  function pickMeasureTool(id) {
    if (!hasScale) {
      setMeasureMenuOpen(false);
      setCalibrationModalOpen(true);
      return;
    }
    setActiveTool(id);
    setMeasureMenuOpen(false);
  }

  function openCalibration() {
    setCalibrationModalOpen(true);
  }

  function handleZoomIn()    { setZoom(zoom * 1.25); }
  function handleZoomOut()   { setZoom(zoom / 1.25); }
  function handleZoomReset() { setZoom(1); useStore.getState().setPan({ x: 0, y: 0 }); }

  const isFill   = editingColor === 'fill';
  const isStroke = editingColor === 'stroke';
  const currentPickColor = isFill ? (activeFillColor || 'transparent') : activeColor;

  function pickColor(c) {
    if (isFill) setActiveFillColor(c);
    else setActiveColor(c);
    setEditingColor(null);
  }

  function pickNone() {
    setActiveFillColor('transparent');
    setEditingColor(null);
  }

  const fillIsTransparent = !activeFillColor || activeFillColor === 'transparent';

  return (
    <div className={styles.toolbar}>
      {/* Tool buttons */}
      <div className={styles.toolsGroup}>
        {TOOLS.map(({ id, label, icon: Icon, readonlyOk }) => {
          // 'pan' and any tool flagged readonlyOk (e.g., 'select') are
          // available to readonly users — they're viewing aids, not
          // editing tools.
          const isMarkupTool = id !== 'pan' && !readonlyOk;
          const disabled = isMarkupTool && !canMarkup;
          return (
            <button
              key={id}
              className={`${styles.toolBtn} ${activeTool === id ? styles.toolActive : ''}`}
              onClick={() => !disabled && setActiveTool(id)}
              title={disabled ? `${label} (read-only)` : label}
              disabled={disabled}
            >
              <Icon />
            </button>
          );
        })}

        {/* Shape family — single toolbar slot with a flyout for rect
            (drag-out), polygon (click-to-add-vertex closed), and
            ellipse (drag-out bounding-box ellipse). Same flyout pattern
            as line + text + measure. */}
        <div className={styles.measureWrap} ref={shapeBtnRef}>
          <button
            className={`${styles.toolBtn} ${shapeActive ? styles.toolActive : ''}`}
            onClick={() => {
              if (!canMarkup) return;
              setShapeMenuOpen((v) => !v);
            }}
            title={canMarkup ? 'Rect / Polygon / Ellipse' : 'Shape (read-only)'}
            disabled={!canMarkup}
          >
            <ActiveShapeIcon />
          </button>

          {shapeMenuOpen && (
            <div className={styles.measureFlyout}>
              <div className={styles.measureGroup}>
                <div className={styles.measureGroupLabel}>Shape</div>
                <div className={styles.measureRow}>
                  {SHAPE_TOOLS.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      className={`${styles.measureItem} ${activeTool === id ? styles.measureItemActive : ''}`}
                      onClick={() => pickShapeTool(id)}
                      title={label}
                    >
                      <Icon />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Line family — single toolbar slot with a flyout for plain line
            (two-point click-click), polyline (click-to-add-vertex,
            Enter / dbl-click finalizes), and freehand. Same flyout
            pattern as text + measure; the button icon mirrors whichever
            line variant is currently active. */}
        <div className={styles.measureWrap} ref={lineBtnRef}>
          <button
            className={`${styles.toolBtn} ${lineActive ? styles.toolActive : ''}`}
            onClick={() => {
              if (!canMarkup) return;
              setLineMenuOpen((v) => !v);
            }}
            title={canMarkup ? 'Line / Polyline / Freehand' : 'Line (read-only)'}
            disabled={!canMarkup}
          >
            <ActiveLineIcon />
          </button>

          {lineMenuOpen && (
            <div className={styles.measureFlyout}>
              <div className={styles.measureGroup}>
                <div className={styles.measureGroupLabel}>Line</div>
                <div className={styles.measureRow}>
                  {LINE_TOOLS.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      className={`${styles.measureItem} ${activeTool === id ? styles.measureItemActive : ''}`}
                      onClick={() => pickLineTool(id)}
                      title={label}
                    >
                      <Icon />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Text family — single toolbar slot with a flyout for plain text,
            callout (rect + leader + arrow), and cloud-callout (cloud +
            leader). The button itself activates the most recently picked
            text tool when clicked single-time without opening; the flyout
            lets the user switch between variants. */}
        <div className={styles.measureWrap} ref={textBtnRef}>
          <button
            className={`${styles.toolBtn} ${textActive ? styles.toolActive : ''}`}
            onClick={() => {
              if (!canMarkup) return;
              setTextMenuOpen((v) => !v);
            }}
            title={canMarkup ? 'Text / Callout' : 'Text (read-only)'}
            disabled={!canMarkup}
          >
            <TextIcon />
          </button>

          {textMenuOpen && (
            <div className={styles.measureFlyout}>
              <div className={styles.measureGroup}>
                <div className={styles.measureGroupLabel}>Text</div>
                <div className={styles.measureRow}>
                  {TEXT_TOOLS.map(({ id, label, icon: Icon }) => (
                    <button
                      key={id}
                      className={`${styles.measureItem} ${activeTool === id ? styles.measureItemActive : ''}`}
                      onClick={() => pickTextTool(id)}
                      title={label}
                    >
                      <Icon />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Measure tool with flyout for length/area/volume sub-tools */}
        <div className={styles.measureWrap} ref={measureBtnRef}>
          <button
            className={`${styles.toolBtn} ${measureActive ? styles.toolActive : ''}`}
            onClick={() => {
              if (!canMarkup) return;
              if (!hasScale) { setCalibrationModalOpen(true); return; }
              setMeasureMenuOpen(v => !v);
            }}
            title={canMarkup ? (hasScale ? 'Measure' : 'Set scale first') : 'Measure (read-only)'}
            disabled={!canMarkup}
          >
            <MeasureIcon />
          </button>

          {measureMenuOpen && (
            <div className={styles.measureFlyout}>
              {MEASURE_GROUPS.map(({ group, tools }) => (
                <div key={group} className={styles.measureGroup}>
                  <div className={styles.measureGroupLabel}>{group}</div>
                  <div className={styles.measureRow}>
                    {tools.map(({ id, label, icon: Icon }) => (
                      <button
                        key={id}
                        className={`${styles.measureItem} ${activeTool === id ? styles.measureItemActive : ''}`}
                        onClick={() => pickMeasureTool(id)}
                        title={label}
                      >
                        <Icon />
                        <span>{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.divider} />

      {/* Scale chip — click to open CalibrationModal */}
      {canMarkup && activeSheet && (
        <div className={styles.scaleGroup}>
          <button
            className={`${styles.scaleChip} ${hasScale ? styles.scaleChipSet : ''}`}
            onClick={openCalibration}
            title={hasScale ? `Scale: ${calibration.label}` : 'No scale — click to calibrate'}
          >
            <span className={styles.scaleChipIcon}><RulerIcon /></span>
            <span className={styles.scaleChipLabel}>
              {hasScale ? scaleChipLabel(calibration) : 'No scale'}
            </span>
          </button>
        </div>
      )}

      <div className={styles.divider} />

      {/* Zoom controls */}
      <div className={styles.zoomGroup}>
        <button className={styles.zoomBtn} onClick={handleZoomOut} title="Zoom out">
          <MinusIcon />
        </button>
        <button className={styles.zoomLabel} onClick={handleZoomReset} title="Reset zoom">
          {Math.round(zoom * 100)}%
        </button>
        <button className={styles.zoomBtn} onClick={handleZoomIn} title="Zoom in">
          <PlusIcon />
        </button>
      </div>

      <div className={styles.spacer} />

      {/* Color section */}
      {canMarkup && (
        <div className={styles.colorSection} ref={colorSectionRef}>
          {/* Color picker popup — floats LEFT of toolbar */}
          {editingColor && (
            <div className={styles.pickerPopup}>
              <div className={styles.pickerTitle}>
                {isFill ? 'FILL' : 'STROKE'}
              </div>
              <div className={styles.presetGrid}>
                {isFill && (
                  <button
                    className={`${styles.presetSwatch} ${styles.nonePreset} ${fillIsTransparent ? styles.presetActive : ''}`}
                    onClick={pickNone}
                    title="No fill"
                  />
                )}
                {PRESET_COLORS.map((c) => (
                  <button
                    key={c}
                    className={`${styles.presetSwatch} ${currentPickColor === c ? styles.presetActive : ''}`}
                    style={{ background: c }}
                    onClick={() => pickColor(c)}
                    title={c}
                  />
                ))}
              </div>
              <label className={styles.customRow}>
                <span className={styles.customLabel}>Custom</span>
                <input
                  type="color"
                  ref={isFill ? customFillRef : customStrokeRef}
                  value={currentPickColor === 'transparent' ? '#ffffff' : (currentPickColor || '#e8a020')}
                  onChange={(e) => { isFill ? setActiveFillColor(e.target.value) : setActiveColor(e.target.value); }}
                  onBlur={() => setEditingColor(null)}
                  className={styles.customColorInput}
                />
              </label>
              {isFill && !fillIsTransparent && (
                <div className={styles.opacityRow}>
                  <span className={styles.customLabel}>Opacity</span>
                  <input
                    type="range" min="0" max="1" step="0.05"
                    value={activeFillOpacity}
                    className={styles.opacitySlider}
                    onChange={(e) => setActiveFillOpacity(+e.target.value)}
                  />
                  <span className={styles.opacityVal}>{Math.round(activeFillOpacity * 100)}%</span>
                </div>
              )}
            </div>
          )}

          <span className={styles.colorLabel}>COLOR</span>

          {/* Stroke chip */}
          <div className={styles.chipRow}>
            <span className={styles.chipLetter}>S</span>
            <button
              className={`${styles.colorChip} ${isStroke ? styles.chipActive : ''}`}
              style={{ background: activeColor }}
              onClick={() => setEditingColor(isStroke ? null : 'stroke')}
              title={`Stroke: ${activeColor}`}
            />
          </div>

          {/* Fill chip */}
          <div className={styles.chipRow}>
            <span className={styles.chipLetter}>F</span>
            <button
              className={`${styles.colorChip} ${isFill ? styles.chipActive : ''} ${fillIsTransparent ? styles.chipTransparent : ''}`}
              style={fillIsTransparent ? {} : { background: activeFillColor }}
              onClick={() => setEditingColor(isFill ? null : 'fill')}
              title={fillIsTransparent ? 'Fill: none' : `Fill: ${activeFillColor}`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Icons ──
function PanIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M8 1v14M1 8h14M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" opacity="0.5"/>
      <circle cx="8" cy="8" r="2.5" fill="currentColor"/>
    </svg>
  );
}
function SelectIcon() {
  // Arrow pointer + dashed selection rect — drag-to-select-many.
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="2" width="9" height="9" rx="1"
        stroke="currentColor" strokeWidth="1.1" strokeDasharray="1.6 1.4" fill="none"/>
      <path d="M8.5 8.5L14 14M14 14L11 13.2M14 14L13.2 11"
        stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  );
}
function ArrowIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M3 13L13 3M13 3H8M13 3V8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function CloudIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M3 10c0-1.1.7-2 1.7-2.3C4.9 5.6 6.8 4 9 4c2.2 0 4 1.8 4 4a3 3 0 010 6H4a3 3 0 01-1-5.8" stroke="currentColor" strokeWidth="1.3"/>
    </svg>
  );
}
function RectIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="4" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="1.3"/>
    </svg>
  );
}
function LineIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M2 14L14 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
function TextIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M3 4h10M8 4v8M5 12h6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
    </svg>
  );
}
function CalloutIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <rect x="2" y="3" width="9" height="6" rx="1" stroke="currentColor" strokeWidth="1.3"/>
      <path d="M9 9l5 5M14 14l-2-1M14 14l1-2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function CloudCalloutIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M2 5c0-.8.6-1.5 1.4-1.6C3.6 2.6 4.5 2 5.5 2c1 0 1.8.6 2.1 1.5A2 2 0 0110 5.5 1.5 1.5 0 019.5 8.5h-6A1.5 1.5 0 012 7c0-.8.5-1.5 1.2-1.7"
        stroke="currentColor" strokeWidth="1.2"/>
      <path d="M8 9l5 5M14 14l-2-1M14 14l1-2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}
function FreehandIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M2 12c1-2 2-6 4-7s3 3 5 2 2-4 3-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" fill="none"/>
    </svg>
  );
}
function MeasureIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M2 8h12M2 5v6M14 5v6M6 6v4M10 6v4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
    </svg>
  );
}
function RulerIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
      <path d="M1 11l10-10 4 4L5 15z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/>
      <path d="M4 8l1.5 1.5M6 6l1.5 1.5M8 4l1.5 1.5M10 2l1.5 1.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
    </svg>
  );
}
function PolylineIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M2 12L6 6L10 9L14 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="2" cy="12" r="1.2" fill="currentColor"/>
      <circle cx="6" cy="6" r="1.2" fill="currentColor"/>
      <circle cx="10" cy="9" r="1.2" fill="currentColor"/>
      <circle cx="14" cy="3" r="1.2" fill="currentColor"/>
    </svg>
  );
}
function PolygonIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <path d="M8 2L14 6L12 13L4 13L2 6Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" fill="none"/>
    </svg>
  );
}
function EllipseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
      <ellipse cx="8" cy="8" rx="6" ry="4.5" stroke="currentColor" strokeWidth="1.3" fill="none"/>
    </svg>
  );
}
function MinusIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M2 6h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
function PlusIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M6 2v8M2 6h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}
